import { Article, Comment, Person, ProfilePage, SocialMediaPosting, WithContext } from "schema-dts";

import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import { ProfileDetailData } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers";
import { getPostDate } from "@/mk2/apps/blogs/helpers";
import {
    blogsAlbumDetailUrl,
    blogsAlbumPhotoUrl,
    blogsAutoMessagePhotoUrl,
    blogsAutoMessageUrl,
    blogsProfileUrl,
    blogsShareMessageUrl,
    blogsShortMessagePhotoUrl,
    blogsShortMessageUrl,
} from "@/mk2/apps/blogs/urls";
import { articleUrl as blogsArticleUrl } from "@/mk2/apps/blogs/utils";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { entityToSchemaOrg } from "@/mk2/apps/wiki/structuredData";
import { getAllPlaintext, getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import {
    getCoverOrFirstPhoto,
    getPhotoResizedDimensions,
    getUrlOfCoverImageOrFirstImageOrFallback,
    getUrlOfFirstImageOrFallback,
    organizationModrykonikWithoutContext,
} from "@/mk2/helpers/structuredData";
import { YMDHMSIso } from "@/mk2/helpers/timeformat";
import { fbMorykonikProfileUrl, url } from "@/mk2/helpers/urls";
import {
    BlogPostEntity,
    BlogPostType,
    CommentEntity,
    ImageEntity,
    PostVisibility,
} from "@/mk2/schemas";
import { photoUrl } from "@/mk/photo/photoUrl";
import { BASE_URL } from "@/mk/settings";

export function articleBlogs(baseUrl: string, article: BlogPostEntity): WithContext<Article> {
    // according to https://developers.google.com/search/docs/data-types/article#amp
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": baseUrl + blogsArticleUrl(article),
        headline: getArticleTitleAsString(article.titleAST),
        description: getFirstParagraphsPlaintext(article.contentAST),
        image: getUrlOfCoverImageOrFirstImageOrFallback(article.contentAST, article.photos),

        datePublished: article.firstPublishedAt
            ? YMDHMSIso(article.firstPublishedAt)
            : YMDHMSIso(getPostDate(article)),
        dateModified: YMDHMSIso(getPostDate(article)),

        author: {
            "@type": "Person",
            name: article.user.username,
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": baseUrl + blogsArticleUrl(article),
        },
        publisher: organizationModrykonikWithoutContext(),
    };
}
export function articleBlogsOG(baseUrl: string, article: BlogPostEntity) {
    const articleUrl = blogsArticleUrl(article);
    const mainPhoto = getCoverOrFirstPhoto(article.contentAST, article.photos);
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    const title = getArticleTitleAsString(article.titleAST);
    return {
        "og:type": "article",

        "og:title": title,
        "og:description": getFirstParagraphsPlaintext(article.contentAST),

        "og:image": getUrlOfCoverImageOrFirstImageOrFallback(article.contentAST, article.photos),
        "og:image:alt": title,
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + articleUrl,

        "article:author": fbMorykonikProfileUrl(),
        "article:content_tier": "free",

        "article:published_time": article.firstPublishedAt
            ? YMDHMSIso(article.firstPublishedAt)
            : YMDHMSIso(getPostDate(article)),
        "article:modified_time": YMDHMSIso(getPostDate(article)),
        "article:publisher": BASE_URL + homepageUrl,
        ...(article.advancedArticle?.category?.name && {
            "article:section": article.advancedArticle.category.name,
        }),
    };
}

export function shortMessageBlogsOG(baseUrl: string, post: BlogPostEntity) {
    const username = post.user?.username || post.author?.username;
    const postSlug = post.slug;

    const pageUrl =
        post.type === BlogPostType.AUTOMATIC_MESSAGE
            ? url(blogsAutoMessageUrl, { username, postSlug })
            : post.type === BlogPostType.SHARE
              ? url(blogsShareMessageUrl, { username, postSlug })
              : url(blogsShortMessageUrl, { username, postSlug });

    // Short messages store photos in images[].photo, not in photos[]
    const mainPhoto = post.images?.length > 0 ? post.images[0].photo : null;
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    return {
        "og:type": "website",

        "og:image": getUrlOfFirstImageOrFallback(mainPhoto),
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + pageUrl,
    };
}

export function albumBlogsOG(baseUrl: string, post: BlogPostEntity) {
    const username = post.user?.username || post.author?.username;
    const postSlug = post.slug;

    const pageUrl = url(blogsAlbumDetailUrl, { username, postSlug });

    // Use first image from album
    const mainPhoto = post.images?.length > 0 ? post.images[0].photo : null;
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    return {
        "og:type": "website",

        "og:image": getUrlOfFirstImageOrFallback(mainPhoto),
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + pageUrl,
    };
}

export function imageBlogsOG(baseUrl: string, post: BlogPostEntity, image: ImageEntity) {
    const pageUrl =
        post.type === BlogPostType.ALBUM
            ? url(blogsAlbumPhotoUrl, {
                  username: post.author.username,
                  postSlug: post.slug,
                  imageId: image.id,
              })
            : post.type === BlogPostType.AUTOMATIC_MESSAGE
              ? url(blogsAutoMessagePhotoUrl, {
                    username: post.author.username,
                    postSlug: post.slug,
                    imageId: image.id,
                })
              : url(blogsShortMessagePhotoUrl, {
                    username: post.author.username,
                    postSlug: post.slug,
                    imageId: image.id,
                });

    const dimensions = image?.photo ? getPhotoResizedDimensions(image.photo, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    const title = image.title || (post.perexAST ? getFirstParagraphsPlaintext(post.perexAST) : "");
    return {
        "og:type": "website",

        "og:title": title,
        "og:description": post.contentAST ? getFirstParagraphsPlaintext(post.contentAST) : "",

        "og:image": getUrlOfFirstImageOrFallback(image.photo),
        "og:image:alt": title,
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + pageUrl,
    };
}

// Cap on how many comments are embedded in the JSON-LD. Blog short messages
// render all their comments on a single (non-paginated) page, so this subset is
// still a prefix of what is visible; it bounds the SSR payload for long threads.
const MAX_JSONLD_COMMENTS = 50;

// schema.org SocialMediaPosting for public photoblog posts (short messages,
// albums, shares). interactionStatistic exposes comment and reaction counts.
// Emitted only for publicly indexable posts — these pages are crawlable by
// anonymous visitors and Googlebot, so non-public visibilities / deleted /
// noindex posts are skipped.
export function socialMediaPosting(
    baseUrl: string,
    post: BlogPostEntity,
    comments?: CommentEntity[],
): WithContext<SocialMediaPosting> | null {
    if (post.noindex || post.deletedAt || post.visibility !== PostVisibility.Public) {
        return null;
    }

    const username = post.user?.username || post.author?.username;
    if (!username) {
        return null;
    }

    const route =
        post.type === BlogPostType.ALBUM
            ? blogsAlbumDetailUrl
            : post.type === BlogPostType.AUTOMATIC_MESSAGE
              ? blogsAutoMessageUrl
              : post.type === BlogPostType.SHARE
                ? blogsShareMessageUrl
                : blogsShortMessageUrl;
    const pageUrl = baseUrl + url(route, { username, postSlug: post.slug });

    const bodyText = [post.contentAST, post.perexAST, post.titleAST]
        .filter(Boolean)
        .map((ast) => getAllPlaintext(ast).trim())
        .find(Boolean);
    const mainPhoto = post.images?.[0]?.photo;
    const videos = (post.videoLinks ?? []).map((link) => link.video?.jsonld).filter(Boolean);
    // Google requires an Article/SocialMediaPosting to carry at least one of
    // text / image / video. Content-less posts (e.g. automatic messages with
    // no body, photo or video) must not emit the schema.
    if (!bodyText && !mainPhoto && !videos.length) {
        return null;
    }
    // Article (SocialMediaPosting's parent type) recommends a headline; short
    // messages have no title, so fall back to the start of the body text.
    // Google recommends keeping headline to ~110 chars.
    const headline = post.heading || (bodyText ? bodyText.slice(0, 110) : undefined);

    // schema.org Comment list satisfying Google's "Discussion forum" recommended
    // `comment` field. Mirrors the forum's discussionForumPosting(): drop deleted
    // and empty-text comments, keep chronological order (the API returns comments
    // ordered by created_time), cap at MAX_JSONLD_COMMENTS. Anonymous comments
    // (no author username) still carry their text but omit the author. Comment
    // bodies are plain text (getAllPlaintext), and `<` is escaped centrally when
    // the JSON-LD is serialised in Page.tsx, so `</script>` / HTML is safe.
    const commentItems: Comment[] = (comments || [])
        .filter((c) => !c.deletedAt)
        .reduce<Comment[]>((acc, c) => {
            if (acc.length >= MAX_JSONLD_COMMENTS) return acc;
            const text = c.textAST ? getAllPlaintext(c.textAST).trim() : "";
            if (!text) return acc;
            // Google requires author.name on every nested Comment, so skip the
            // rare author-less comments (deleted auth_user / as_admin-hidden)
            // rather than emit an invalid Comment node.
            const commentAuthor = c.authorUser?.username;
            if (!commentAuthor) return acc;
            acc.push({
                "@type": "Comment",
                text,
                datePublished: YMDHMSIso(c.created),
                url: pageUrl + "#comment-" + c.id,
                author: {
                    "@type": "Person",
                    name: commentAuthor,
                    url: baseUrl + url(blogsProfileUrl, { username: commentAuthor }),
                },
            });
            return acc;
        }, []);

    return {
        "@context": "https://schema.org",
        "@type": "SocialMediaPosting",
        "@id": pageUrl + "#post",
        url: pageUrl,
        ...(headline && { headline }),
        ...(bodyText && { text: bodyText }),
        ...(mainPhoto && { image: photoUrl(mainPhoto, "s1600x1600") }),
        ...(videos.length && { video: videos as any }),
        datePublished: YMDHMSIso(post.createdAt || post.created),
        author: {
            "@type": "Person",
            name: username,
            url: baseUrl + url(blogsProfileUrl, { username }),
        },
        interactionStatistic: [
            {
                "@type": "InteractionCounter",
                interactionType: { "@type": "CommentAction" },
                userInteractionCount: post.commentsCount || 0,
            },
            {
                "@type": "InteractionCounter",
                interactionType: { "@type": "LikeAction" },
                userInteractionCount:
                    post.likeable?.reactionsTotalCount || post.likeable?.likesCount || 0,
            },
        ],
        ...(commentItems.length > 0 && { comment: commentItems }),
    };
}

// Google "Profile page" structured data for a member's public profile:
// ProfilePage wrapping a Person. interactionStatistic exposes how many members
// follow this person; agentInteractionStatistic how much content they authored
// (articles + short messages + albums).
// https://developers.google.com/search/docs/appearance/structured-data/profile-page
export function profilePagePerson(
    baseUrl: string,
    data: ProfileDetailData,
): WithContext<ProfilePage> | null {
    const username = data.user?.username;
    if (!username || data.userIsInactive) {
        return null;
    }

    const stats = data.userStats;
    const authoredCount =
        (stats?.blogsArticlesCount || 0) +
        (stats?.blogsShortMessagesCount || 0) +
        (stats?.blogsAlbumsCount || 0);

    const person: Person = {
        "@type": "Person",
        name: username,
        url: baseUrl + url(blogsProfileUrl, { username }),
        ...(data.userAvatarPhoto && { image: photoUrl(data.userAvatarPhoto, "s1600x1600") }),
        ...(data.userAboutMeText && { description: data.userAboutMeText }),
        interactionStatistic: {
            "@type": "InteractionCounter",
            interactionType: { "@type": "FollowAction" },
            userInteractionCount: data.followers?.count || 0,
        },
    };

    // agentInteractionStatistic (content authored BY this person) is valid
    // schema.org but not yet in the installed schema-dts Person type.
    (person as unknown as Record<string, unknown>).agentInteractionStatistic = {
        "@type": "InteractionCounter",
        interactionType: { "@type": "WriteAction" },
        userInteractionCount: authoredCount,
    };

    // Note: userRegisteredOn is a display string ("M/YYYY"), not an ISO date, so
    // it is deliberately NOT emitted as dateCreated (schema.org expects ISO 8601).
    return {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        mainEntity: person,
    };
}

export function entitiesForProfile(profileDetailData: ProfileDetailData): object[] {
    const links = profileDetailData.profileEntityLinks;
    if (!links?.length) return [];

    const geo = profileDetailData.userGeo;

    const typeMap: Record<string, string> = {
        brand: "Brand",
        organization: "Organization",
        medical_organization: "MedicalOrganization",
        local_business: "LocalBusiness",
    };

    return links.map((link) => {
        const e = link.entity;
        const schemaType = typeMap[e.entityType] || "Organization";
        const sameAs = [...(e.sameAsUrls || [])];
        if (e.wikidataQid) sameAs.push(`https://www.wikidata.org/wiki/${e.wikidataQid}`);

        const result: Record<string, any> = {
            "@context": "https://schema.org",
            "@type": schemaType,
            name: e.name,
            ...(e.url && { url: e.url }),
            ...(e.description && { description: e.description }),
            ...(sameAs.length && { sameAs }),
        };

        // Add address and geo for location-aware entity types
        if (
            geo &&
            (schemaType === "LocalBusiness" ||
                schemaType === "Organization" ||
                schemaType === "MedicalOrganization")
        ) {
            if (geo.placeName || geo.countyName || geo.countryName) {
                result.address = {
                    "@type": "PostalAddress",
                    ...(geo.placeName && { addressLocality: geo.placeName }),
                    ...(geo.countyName && { addressRegion: geo.countyName }),
                    ...(geo.countryName && { addressCountry: geo.countryName }),
                };
            }
            if (geo.lat != null && geo.lon != null) {
                result.geo = {
                    "@type": "GeoCoordinates",
                    latitude: geo.lat,
                    longitude: geo.lon,
                };
            }
        }

        return result;
    });
}

export function entitiesForPost(post: BlogPostEntity): object[] {
    if (!post.entityLinks?.length) return [];

    return post.entityLinks.map((link) => ({
        "@context": "https://schema.org",
        ...entityToSchemaOrg(link.entity),
    }));
}

export function videoObjectsForPost(post: BlogPostEntity): object[] {
    if (!post.videoLinks?.length) return [];
    return post.videoLinks.map((link) => ({
        "@context": "https://schema.org",
        ...link.video.jsonld,
    }));
}
